import React, { useCallback, useEffect, useRef, useState } from 'react'
import deepEqual from 'react-fast-compare'
import { Button, Flex, Popover, Space, Tooltip } from 'antd'
import { Loading3QuartersOutlined, PartitionOutlined, WarningOutlined } from '@ant-design/icons'
import { DeliveryChannel, getDeliveryChannelLongName } from '@pushly/models/lib/enums/delivery-channels.enum'
import { usePQueryBuilder } from '@pushly/aqe'
import { numberWithCommas } from '../../../_utils/utils'
import { getUnsafeExecutionText } from './unsafe-execution'
import { isUnsafeExecution } from './unsafe-execution'
import type { ReachCalculatedField } from './unsafe-execution'
import { PendingEdit, stalenessOf } from './pending-edit'

/**
 * The domain reach lookup, injected so this sibling stays a pure reader of the
 * live document. The one return type the domain builder uses is
 * `count_by_channel`; the count-only overload is a different surface's and is not
 * threaded here.
 */
export type EstimateReach = (
    domainId: number,
    query: unknown,
    targetedChannels: string[],
    returnType: 'count_by_channel',
    opts?: { cancellationKey?: string },
) => Promise<{ ok: boolean; data?: unknown; cancelled?: boolean }>

export interface SegmentReachEstimateProps {
    domainId: number
    /** The segment's own channels; empty means every channel. */
    targetedChannels: string[]
    calculatedFields: readonly ReachCalculatedField[]
    estimateReach: EstimateReach
}

type ReachState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'unsafe' }
    | { status: 'error' }
    | { status: 'success'; total: number; byChannel: Record<string, number> }

const DEBOUNCE_MS = 320

let instanceCounter = 0

/**
 * A live audience-size estimate for the domain segment being built, composed into
 * the conditions well's actions slot beside the builder (the count-preview
 * pattern). It reads the current document straight from the builder and never
 * holds a copy of it.
 *
 * States:
 *  - idle: no conditions yet — renders nothing, like count-preview
 *  - loading: an estimate is in flight
 *  - success: the total and a per-channel breakdown
 *  - error: the lookup failed, offered with a retry — never a silent zero, which a
 *    real result of 0 is indistinguishable from otherwise
 *  - unsafe: a delayed property the platform has not computed yet — the size is
 *    masked and no lookup is issued, because the number would be wrong
 */
export const SegmentReachEstimate: React.FC<SegmentReachEstimateProps> = ({
    domainId,
    targetedChannels,
    calculatedFields,
    estimateReach,
}) => {
    const { isValid, conditionCount, serialize, revision, editingCount } = usePQueryBuilder()

    // Read at call time, not captured, so the debounced fetch always serializes
    // the document as it stands when the timer fires rather than when it was set.
    const serializeRef = useRef(serialize)
    serializeRef.current = serialize

    const [state, setState] = useState<ReachState>({ status: 'idle' })

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // The last query a request was issued for — the document and the channels it was
    // costed against — compared to skip a refetch when nothing about the request
    // would differ. The channels are part of the request, so a change to them alone
    // is a different question even when the document is byte-identical.
    const lastFetchedRef = useRef<{ document: Record<string, unknown>; targetedChannels: string[] } | null>(null)
    // Bumped for every request and every abandonment, so a response that resolves
    // after a newer one was issued can tell it has been superseded and drop itself
    // rather than overwrite the newer result.
    const generationRef = useRef(0)
    // Stable per instance so the service's own cancellation cancels the previous
    // in-flight request the moment a newer one starts.
    const cancellationKeyRef = useRef<string>()
    if (!cancellationKeyRef.current) {
        cancellationKeyRef.current = `segment-reach-estimate-${(instanceCounter += 1)}`
    }

    const runEstimate = useCallback(
        async (document: Record<string, unknown>) => {
            const generation = (generationRef.current += 1)
            lastFetchedRef.current = { document, targetedChannels }
            setState({ status: 'loading' })

            const res = await estimateReach(
                domainId,
                { filters: document, domainId },
                targetedChannels,
                'count_by_channel',
                { cancellationKey: cancellationKeyRef.current },
            )

            // A newer request has started, or this one was cancelled in favor of
            // it — either way its result is stale and must not be shown.
            if (generation !== generationRef.current || res.cancelled) return

            if (!res.ok) {
                setState({ status: 'error' })
                return
            }

            setState({ status: 'success', ...summarize(res.data) })
        },
        [domainId, targetedChannels, estimateReach],
    )

    useEffect(() => {
        if (conditionCount === 0) {
            // Nothing to estimate. Abandon any in-flight response and clear the
            // dedupe memory so the next real query fetches.
            generationRef.current += 1
            lastFetchedRef.current = null
            setState({ status: 'idle' })
            return
        }

        // A condition being edited is in the document already — the editor writes every keystroke
        // through so the row and its summary stay live — but it is not yet what the reader has asked
        // for. Half of a city name is a different audience from the city, and costing it means a
        // blowhole invocation per pause in typing for a number that was never meaningful. So the
        // estimate waits for the edit to be committed, and says so rather than presenting the
        // pre-edit number as though it still described the segment.
        if (editingCount > 0) return

        // An invalid document does not describe a query the endpoint can answer;
        // leave whatever is already shown in place and wait for it to be fixed.
        if (!isValid) return

        const document = serializeRef.current()

        if (isUnsafeExecution(document, calculatedFields)) {
            // The number would be wrong until the platform computes it, so it is
            // masked and no request is issued. Clearing the dedupe memory means a
            // later edit that makes the segment safe fetches rather than dedupes
            // against the last safe query.
            generationRef.current += 1
            lastFetchedRef.current = null
            setState({ status: 'unsafe' })
            return
        }

        if (lastFetchedRef.current && deepEqual({ document, targetedChannels }, lastFetchedRef.current)) return

        if (debounceRef.current !== null) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null
            runEstimate(document)
        }, DEBOUNCE_MS)

        return () => {
            if (debounceRef.current !== null) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
        }
        // `revision` stands in for the document identity: it moves on every builder
        // mutation, including meta-only edits that leave `queryHash` unchanged.
        // `editingCount` is watched alongside it because committing an edit does not mutate the
        // document — the keystrokes already did — so closing the editor moves only this, and it is
        // what releases the estimate.
    }, [revision, editingCount, conditionCount, isValid, calculatedFields, runEstimate])

    const handleRetry = useCallback(() => {
        lastFetchedRef.current = null
        runEstimate(serializeRef.current())
    }, [runEstimate])

    const staleness = stalenessOf(editingCount)

    if (state.status === 'idle') {
        // Nothing has been estimated yet and an edit is in flight, which is the first condition on a
        // new segment. Saying what will happen beats an empty space beside a condition the reader can
        // see, and beats a number for a value they are still choosing.
        if (staleness === 'pending-edit' && conditionCount > 0) {
            return <span className="reach-label">Reach updates when you save this condition</span>
        }

        return null
    }

    if (state.status === 'loading') {
        return (
            <Flex align="center" gap={8}>
                <Loading3QuartersOutlined spin={true} />
                <span>Calculating reach…</span>
            </Flex>
        )
    }

    if (state.status === 'unsafe') {
        return (
            <PendingEdit staleness={staleness}>
                <Space size={4}>
                    <span className="reach-tooltip">
                        <Tooltip title={getUnsafeExecutionText()}>
                            <WarningOutlined className="info-icon error-icon" />
                        </Tooltip>
                    </span>
                    <span className="reach-estimate">--</span>
                    <span className="reach-label">Subscribers</span>
                </Space>
            </PendingEdit>
        )
    }

    if (state.status === 'error') {
        return (
            <Flex align="center" gap={8}>
                <span>Reach is currently not available</span>
                <Button type="link" size="small" onClick={handleRetry}>
                    Retry
                </Button>
            </Flex>
        )
    }

    const channels = Object.keys(state.byChannel)
    // The per-channel breakdown is meaningless when nobody is reached.
    const showBreakdown = state.total !== 0 && channels.length > 0

    return (
        <PendingEdit staleness={staleness}>
            <Space size={4}>
                <span className="reach-estimate">{numberWithCommas(state.total)}</span>
                <span className="reach-label">Subscribers</span>
                {showBreakdown && (
                    <Popover
                        title="Reach Estimates by Channel"
                        overlayClassName="audience-channel-breakdown-popover"
                        content={channels.map((channel) => (
                            <div key={channel} className="reach-breakdown-row">
                                <span className="reach-breakdown-label">{channelLabel(channel)}:</span>
                                <span className="reach-breakdown-value">
                                    {numberWithCommas(state.byChannel[channel])}
                                </span>
                            </div>
                        ))}
                        placement="left"
                    >
                        <PartitionOutlined />
                    </Popover>
                )}
            </Space>
        </PendingEdit>
    )
}

/**
 * Fold the snake-cased per-channel response into a total and a keyed breakdown.
 * The service has already re-cased the keys to snake (`native_ios`), so they are
 * indexed as delivered rather than re-derived.
 */
function summarize(data: unknown): { total: number; byChannel: Record<string, number> } {
    if (typeof data === 'number') return { total: data, byChannel: {} }

    const byChannel: Record<string, number> = {}
    let total = 0

    if (data && typeof data === 'object') {
        for (const [channel, value] of Object.entries(data as Record<string, number>)) {
            total += value
            byChannel[channel] = value
        }
    }

    return { total, byChannel }
}

function channelLabel(snakeKey: string): string {
    const channel = DeliveryChannel[snakeKey.toUpperCase() as keyof typeof DeliveryChannel]
    return channel ? getDeliveryChannelLongName(channel) : snakeKey
}
